import { Controller } from "@hotwired/stimulus"
import tippy from "tippy.js"

// Renders an expandable list of inputs written to a single hidden form field.
// The visible inputs themselves are unnamed — they're scratch space; only the
// hidden field is submitted.
//
// Default mode joins the trimmed values with `delimiterValue`. Known
// limitation: a value containing the delimiter cannot round-trip, so callers
// should pick one that won't appear in their values (comma works for API keys).
//
// When `withItemLabelsValue` is set, each row also carries a free-text label
// and rows are written as a JSON array of {label, value} objects. Labels can
// contain anything, which is why that mode doesn't use a delimiter at all.
// Must stay in sync with MultiCredentialCodec on the Ruby side.
export default class extends Controller {
  static targets = ["container", "template", "hiddenField", "input", "itemLabel", "removeButton", "label"]
  static values = {
    delimiter: { type: String, default: "," },
    minItems: { type: Number, default: 1 },
    withItemLabels: { type: Boolean, default: false },
  }

  connect() {
    this.assignRowIds()
    this.initRemoveButtonTooltips()
    this.sync()
    this.enforceMinItems()
  }

  add(event) {
    event.preventDefault()

    const fragment = this.templateTarget.content.cloneNode(true)
    this.containerTarget.appendChild(fragment)

    this.assignRowIds()
    this.initRemoveButtonTooltips()
    this.sync()
    this.enforceMinItems()

    // The credential comes before its optional label, so the caret
    // lands on the value. Naming it is a second step, not the first.
    const newInput = this.inputTargets[this.inputTargets.length - 1]

    if (newInput) {
      newInput.focus()
    }
  }

  remove(event) {
    event.preventDefault()

    if (this.inputTargets.length <= this.minItemsValue) return

    const button = event.currentTarget
    const row = button.closest("[data-multi-text-input-row]")
    if (!row) return

    // Only the first row carries HTML5 `required` (see the component's
    // render_row comment). If we're removing that row, promote the
    // requirement to whichever row is about to become first, so users can't
    // strip the browser's "at least one value" gate by deleting the first
    // input. Rows added via `add()` come from the <template> and never have
    // `required`, so we don't need any equivalent handling there.
    const removedInput = row.querySelector("[data-multi-text-input-target='input']")
    const wasRequired = removedInput?.required === true

    row.remove()

    if (wasRequired && this.inputTargets[0]) {
      this.inputTargets[0].required = true
    }

    this.sync()
    this.enforceMinItems()
  }

  sync() {
    this.hiddenFieldTarget.value = this.withItemLabelsValue
      ? this._labeledPayload()
      : this._joinedValues()
  }

  _joinedValues() {
    return this.inputTargets
      .map((input) => input.value.trim())
      .filter((value) => value.length > 0)
      .join(this.delimiterValue)
  }

  // A row with a label but no value isn't a credential yet, so it's dropped —
  // the reverse (a value with no label) is kept, since labels are optional.
  _labeledPayload() {
    const entries = this.inputTargets
      .map((input) => ({ label: this._itemLabelFor(input), value: input.value.trim() }))
      .filter((entry) => entry.value.length > 0)

    return entries.length === 0 ? "" : JSON.stringify(entries)
  }

  _itemLabelFor(input) {
    const row = input.closest("[data-multi-text-input-row]")
    const labelInput = row?.querySelector("[data-multi-text-input-target='itemLabel']")
    return labelInput ? labelInput.value.trim() : ""
  }

  assignRowIds() {
    this.inputTargets.forEach((input) => {
      if (!input.id) {
        input.id = this._nextRowId()
      }
      const row = input.closest("[data-multi-text-input-row]")
      const label = row?.querySelector("[data-multi-text-input-target='label']")
      if (label && label.htmlFor !== input.id) {
        label.htmlFor = input.id
      }
    })
  }

  initRemoveButtonTooltips() {
    if (this.hasRemoveButtonTarget) {
      tippy(this.removeButtonTargets)
    }
  }

  _nextRowId() {
    if (!this._idBase) {
      this._idBase = `mti-${Math.random().toString(36).slice(2, 9)}`
      this._idCounter = 0
    }
    this._idCounter += 1
    return `${this._idBase}-${this._idCounter}`
  }

  enforceMinItems() {
    const atFloor = this.inputTargets.length <= this.minItemsValue

    if (this.hasRemoveButtonTarget) {
      this.removeButtonTargets.forEach((button) => {
        button.disabled = atFloor
        button.hidden = atFloor
        button.classList.toggle("hidden", atFloor)
      })
    }
  }
}
